import { create } from "zustand";
import { errorMessage } from "@/lib/api";

/* ── Toast ── */

export type Tone = "pine" | "brass" | "rhodo" | "slate";
export type Toast = { id: number; tag: string; text: string; tone: Tone; action?: { label: string; run: () => void } };

type ToastState = {
  toast: Toast | null;
  show: (tag: string, text: string, tone?: Tone, action?: Toast["action"]) => void;
  dismiss: () => void;
};

let timer: ReturnType<typeof setTimeout> | undefined;
let seq = 0;

export const useToast = create<ToastState>((set) => ({
  toast: null,
  show: (tag, text, tone = "pine", action) => {
    clearTimeout(timer);
    set({ toast: { id: ++seq, tag, text, tone, action } });
    // A toast with something to press stays long enough to reach it.
    timer = setTimeout(() => set({ toast: null }), action ? 7000 : 3400);
  },
  dismiss: () => set({ toast: null }),
}));

export const toast = {
  saved: (text: string) => useToast.getState().show("SAVED", text, "pine"),
  queued: (text: string) => useToast.getState().show("QUEUED", text, "brass"),
  resolved: (text: string) =>
    useToast.getState().show("RESOLVED", text, "pine"),
  cancelled: (text: string) =>
    useToast.getState().show("CANCELLED", text, "rhodo"),
  error: (text: string) => useToast.getState().show("NOT SAVED", text, "rhodo"),
  info: (tag: string, text: string) =>
    useToast.getState().show(tag, text, "slate"),
  /** Done at once, with a way back while the toast is up. */
  undoable: (text: string, undo: () => void) => useToast.getState().show("SAVED", text, "pine", { label: "Undo", run: undo }),
};

/** Wrap an async action with error → toast, for one-tap buttons. */
export async function withToast<T>(
  fn: () => Promise<T>,
): Promise<T | undefined> {
  try {
    return await fn();
  } catch (e) {
    toast.error(errorMessage(e));
    return undefined;
  }
}

/* ── Confirm dialog: every sensitive action names its consequence ── */

export type ConfirmRequest = {
  title: string;
  body: string;
  /** Names the action: "Yes, cancel B-4470", "Disable account". */
  verb: string;
  keep?: string;
  /** Require typing this exact text to enable the verb button. */
  typeToConfirm?: string;
  tone?: "danger" | "primary";
  run: () => unknown;
};

type ConfirmState = {
  request: ConfirmRequest | null;
  seq: number;
  ask: (r: ConfirmRequest) => void;
  close: () => void;
};

export const useConfirm = create<ConfirmState>((set) => ({
  request: null,
  // Counts asks rather than identifying them: the dialog keys on it, so each
  // new question gets a panel with nothing typed and no error left over.
  seq: 0,
  ask: (request) => set((s) => ({ request, seq: s.seq + 1 })),
  close: () => set({ request: null }),
}));

export const confirm = (r: ConfirmRequest) => useConfirm.getState().ask(r);

/* ── A single one-second tick drives every hold countdown ── */

type ClockState = { now: number };
export const useClock = create<ClockState>(() => ({ now: Date.now() }));
if (typeof window !== "undefined") {
  setInterval(() => useClock.setState({ now: Date.now() }), 1000);
}
export const useNow = () => useClock((s) => s.now);
